/**
 * The live automation diagram on Home, ported from v1's Autopilot flow: the kind of HighLevel booking
 * workflow Carlo builds, drawn stage by stage as the page scrolls (form -> confirm -> booked -> wait ->
 * reminder -> showed up? -> outcomes).
 *
 * Coordinates are in the diagram's own 1200 x 600 units: the main path is the top row, the call's
 * outcomes the bottom row. `plane` is who moves it: `lead` = the lead / you, `auto` = the system sends
 * it. `step` is the rail step (0, 1 or 2: the three steps below). Nodes are listed in build order.
 */
export type FlowPlane = 'lead' | 'auto'
export type FlowIcon = 'form' | 'email' | 'booked' | 'reminder' | 'alarm' | 'call' | 'proposal' | 'won' | 'later' | 'nurture' | 'lost'

export type FlowNode = {
  id: string
  title: string
  tag: string
  plane: FlowPlane
  icon: FlowIcon
  x: number
  y: number
  w: number
  h: number
  step: number
  /** One line for the inspector. */
  about: string
}

/** `solid` links are the lead path (arrows a packet runs); `dashed` links reroute it: a reschedule loops back, the call branches out. */
export type FlowLink = { from: string; to: string; style: 'solid' | 'dashed'; label?: string }

const ROWS = [64, 376]
const box = (col: number, row: number) => ({ x: 24 + col * 200, y: ROWS[row]!, w: 156, h: 136 })


export const liveAutomation = {
  eyebrow: 'Live automation',
  titleThin: 'A lead, from form',
  titleBold: 'to booked call.',
  /** The rail beside the diagram: v1's "capture, confirm, remind, then route every outcome". */
  steps: [
    { name: 'Capture', body: 'A form submission starts the flow and confirms by email and SMS.' },
    { name: 'Remind', body: 'The booking moves the pipeline, then reminders go out before the call.' },
    { name: 'Route', body: 'Every outcome is routed, so no lead goes cold.' },
  ],
  panel: { lead: 'Form to', rest: 'Outcome' },
  legend: { auto: 'Automation', lead: 'Lead path' },
  zones: { booking: 'HighLevel workflow', pipeline: 'Pipeline' },
  connected: 'System live',
  hint: { hover: 'Hover a stage to inspect', tap: 'Tap a stage to inspect' },
  nodes: [
    { id: 'form', title: 'Trigger', tag: 'Form submitted', plane: 'lead', icon: 'form', ...box(0, 0), step: 0, about: 'A lead fills in the form and the workflow starts.' },
    { id: 'email', title: 'Confirm', tag: 'Email & SMS', plane: 'auto', icon: 'email', ...box(1, 0), step: 0, about: 'A confirmation goes out by email and SMS straight away.' },
    { id: 'booked', title: 'Pipeline', tag: 'Stage: Booked', plane: 'lead', icon: 'booked', ...box(2, 0), step: 1, about: 'The booking moves the lead to the Booked stage of the pipeline.' },
    { id: 'day', title: 'Wait', tag: '24 hours', plane: 'auto', icon: 'reminder', ...box(3, 0), step: 1, about: 'The workflow waits 24 hours before the next step.' },
    { id: 'hour', title: 'Reminder', tag: '1 hour before', plane: 'auto', icon: 'alarm', ...box(4, 0), step: 1, about: 'A reminder goes out one hour before the call, with a loop back to the booking stage.' },
    { id: 'call', title: 'Showed up?', tag: 'Condition', plane: 'lead', icon: 'call', ...box(5, 0), step: 2, about: 'A condition checks the call outcome and routes the lead.' },
    { id: 'proposal', title: 'Send proposal', tag: 'Next action', plane: 'lead', icon: 'proposal', ...box(0, 1), step: 2, about: 'A lead who is ready gets the proposal as the next action.' },
    { id: 'later', title: 'Not yet', tag: 'Needs time', plane: 'lead', icon: 'later', ...box(3, 1), step: 2, about: 'A lead who needs time is kept warm instead of dropped.' },
    { id: 'lost', title: 'Lost', tag: 'Closed out', plane: 'lead', icon: 'lost', ...box(5, 1), step: 2, about: 'A no is closed out cleanly in the pipeline.' },
    { id: 'won', title: 'Won', tag: 'Client signed', plane: 'lead', icon: 'won', ...box(1, 1), step: 2, about: 'The client signs and the deal is marked won.' },
    { id: 'nurture', title: 'AI nurture', tag: 'Follow-up drip', plane: 'auto', icon: 'nurture', ...box(4, 1), step: 2, about: 'An AI-written follow-up drip keeps in touch until they are ready.' },
  ] satisfies FlowNode[],
  links: [
    { from: 'form', to: 'email', style: 'solid' },
    { from: 'email', to: 'booked', style: 'solid' },
    { from: 'booked', to: 'day', style: 'solid' },
    { from: 'day', to: 'hour', style: 'solid' },
    { from: 'hour', to: 'call', style: 'solid' },
    { from: 'hour', to: 'booked', style: 'dashed', label: 'Loop back' },
    { from: 'call', to: 'proposal', style: 'dashed' },
    { from: 'call', to: 'later', style: 'dashed' },
    { from: 'call', to: 'lost', style: 'dashed' },
    { from: 'proposal', to: 'won', style: 'solid' },
    { from: 'later', to: 'nurture', style: 'solid' },
  ] satisfies FlowLink[],
}
